import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, UserPlus, Check, X, Clock, Inbox, Send, Copy, Hash } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { formatDate } from "../i18n";


const STATUS_LABEL = {
  pending: { cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  accepted: { cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  rejected: { cls: "bg-red-500/15 text-red-600 border-red-500/30" },
  cancelled: { cls: "bg-slate-500/15 text-slate-500 border-slate-500/30" },
  expired: { cls: "bg-slate-500/15 text-slate-500 border-slate-500/30" },
};

export default function WorkspaceJoin({ activeWorkspace: activeWorkspaceProp, onDecision }) {
  const { activeWorkspace: ctxWorkspace } = useAuth();
  const { t } = useTranslation("workspace");
  const [activeWorkspace, setActiveWorkspace] = useState(activeWorkspaceProp || ctxWorkspace || null);
  const isManager = activeWorkspace && ["owner", "admin"].includes(activeWorkspace.role);
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("ref"); // ref | email
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [message, setMessage] = useState("");
  const [mine, setMine] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [busy, setBusy] = useState("");
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [acceptRole, setAcceptRole] = useState("member");

  useEffect(() => {
    if (activeWorkspaceProp) { setActiveWorkspace(activeWorkspaceProp); return; }
    if (ctxWorkspace) { setActiveWorkspace(ctxWorkspace); return; }
    api.get("/account/workspace")
      .then(({ data }) => setActiveWorkspace({ id: data.id, role: data.role, name: data.name, public_ref: data.public_ref }))
      .catch(() => {});
  }, [activeWorkspaceProp, ctxWorkspace]);

  const loadMine = useCallback(async () => {
    try { const { data } = await api.get("/workspaces/join-requests/mine"); setMine(data); } catch (e) {}
  }, []);

  const loadIncoming = useCallback(async () => {
    if (!isManager) { setIncoming([]); return; }
    try { const { data } = await api.get(`/workspaces/${activeWorkspace.id}/join-requests`); setIncoming(data); }
    catch (e) { setIncoming([]); }
  }, [isManager, activeWorkspace]);

  useEffect(() => { loadMine(); loadIncoming(); }, [loadMine, loadIncoming]);

  const doSearch = async () => {
    if (!query.trim()) { toast.error(t("join.needQuery")); return; }
    setSearching(true);
    setResults(null);
    try {
      const param = mode === "ref" ? `ref=${encodeURIComponent(query.trim())}` : `email=${encodeURIComponent(query.trim())}`;
      const { data } = await api.get(`/workspaces/search?${param}`);
      setResults(data);
      if (data.length === 0) toast.info(t("join.noResult"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  const requestJoin = async (workspaceId) => {
    setBusy(workspaceId);
    try {
      const body = { workspace_id: workspaceId, message: message.trim() || undefined };
      if (mode === "ref") body.ref = query.trim();
      else body.admin_email = query.trim();
      await api.post("/workspaces/join-requests", body);
      toast.success(t("join.sent"));
      setResults(null); setQuery(""); setMessage("");
      await loadMine();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const cancelMine = async (id) => {
    setBusy(id);
    try { await api.post(`/workspaces/join-requests/${id}/cancel`); toast.success(t("join.cancelled")); await loadMine(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const decide = async (reqId, accept, role) => {
    setBusy(reqId);
    try {
      await api.post(
        `/workspaces/${activeWorkspace.id}/join-requests/${reqId}/${accept ? "accept" : "reject"}`,
        accept ? { role: role || "member" } : undefined,
      );
      toast.success(accept ? t("join.accepted") : t("join.rejected"));
      setAcceptTarget(null);
      await loadIncoming();
      onDecision?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const pendingIncoming = incoming.filter((r) => r.status === "pending");

  return (
    <div className="space-y-6" data-testid="workspace-join-section">
      {/* Public reference */}
      {activeWorkspace?.public_ref && (
        <div className="glass rounded-2xl p-6" data-testid="workspace-public-ref-card">
          <div className="flex items-center gap-2 mb-3">
            <Hash className="w-5 h-5 text-emerald-500" />
            <h3 className="font-heading text-lg font-semibold">{t("join.refTitle")}</h3>
          </div>
          <div className="flex items-center gap-2">
            <code data-testid="public-ref-value" className="font-mono-token text-base font-semibold text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
              {activeWorkspace.public_ref}
            </code>
            <Button
              data-testid="copy-public-ref-btn" size="sm" variant="outline"
              onClick={() => { navigator.clipboard.writeText(activeWorkspace.public_ref); setCopied(true); toast.success(t("join.refCopied")); setTimeout(() => setCopied(false), 1500); }}
              className="border-slate-200 bg-white hover:bg-slate-100">
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">{t("join.refHelp")}</p>
        </div>
      )}

      {/* Incoming (owner/admin) — historique complet, pending en tête */}
      {isManager && (
        <div className="glass rounded-2xl p-6" data-testid="incoming-join-requests">
          <div className="flex items-center gap-2 mb-4">
            <Inbox className="w-5 h-5 text-emerald-500" />
            <h3 className="font-heading text-lg font-semibold">{t("join.incomingTitle", { count: pendingIncoming.length })}</h3>
          </div>
          {incoming.length === 0 ? (
            <p className="text-sm text-slate-500">{t("join.none")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="incoming-requests-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">{t("join.colRequester")}</th>
                    <th className="py-2 pr-3 font-semibold">{t("join.colDate")}</th>
                    <th className="py-2 pr-3 font-semibold">{t("join.colMode")}</th>
                    <th className="py-2 pr-3 font-semibold">{t("join.colStatus")}</th>
                    <th className="py-2 pr-3 font-semibold">{t("join.colRole")}</th>
                    <th className="py-2 pr-2 font-semibold text-right">{t("join.colActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {incoming.map((jr) => {
                    const st = STATUS_LABEL[jr.status] || STATUS_LABEL.pending;
                    return (
                      <tr key={jr.id} data-testid={`incoming-request-${jr.id}`} className="border-b border-slate-100 last:border-0">
                        <td className="py-2 pr-3">
                          <div className="text-slate-900 truncate max-w-[180px]">{jr.requester_name || jr.requester_email}</div>
                          <div className="text-xs text-slate-500 truncate max-w-[180px]">{jr.requester_email}</div>
                          {jr.message && <div className="text-xs text-slate-500 italic truncate max-w-[200px]">“{jr.message}”</div>}
                        </td>
                        <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatDate(jr.created_at) || "—"}</td>
                        <td className="py-2 pr-3 text-slate-600 text-xs">{jr.lookup_method ? t(`join.lookup.${jr.lookup_method}`, { defaultValue: "—" }) : "—"}</td>
                        <td className="py-2 pr-3"><Badge variant="outline" className={st.cls}>{t(`join.status.${STATUS_LABEL[jr.status] ? jr.status : "pending"}`)}</Badge></td>
                        <td className="py-2 pr-3 text-slate-600 text-xs">
                          {jr.status === "accepted" ? (jr.final_role ? t(`common:roles.${jr.final_role}`, { defaultValue: jr.final_role_label }) : (jr.final_role_label || "—")) : "—"}
                          {jr.processed_at && <div className="text-[10px] text-slate-400">{formatDate(jr.processed_at)}</div>}
                        </td>
                        <td className="py-2 pr-2 text-right whitespace-nowrap">
                          {jr.status === "pending" && (
                            <div className="inline-flex items-center gap-2">
                              <Button data-testid={`accept-request-${jr.id}`} onClick={() => { setAcceptTarget(jr); setAcceptRole("member"); }} disabled={busy === jr.id} size="sm"
                                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button data-testid={`reject-request-${jr.id}`} onClick={() => decide(jr.id, false)} disabled={busy === jr.id} size="sm" variant="outline"
                                className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-600">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Dialog de confirmation d'acceptation avec choix du rôle */}
      <Dialog open={!!acceptTarget} onOpenChange={(o) => !o && setAcceptTarget(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md bg-white overflow-x-hidden" data-testid="accept-role-dialog">
          <DialogHeader>
            <DialogTitle>{t("join.acceptTitle")}</DialogTitle>
            <DialogDescription>
              {acceptTarget ? t("join.acceptDesc", { name: acceptTarget.requester_name || acceptTarget.requester_email }) : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-slate-600">{t("join.role")}</Label>
            <Select value={acceptRole} onValueChange={setAcceptRole}>
              <SelectTrigger data-testid="accept-role-select" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member">{t("common:roles.member")}</SelectItem>
                <SelectItem value="admin">{t("common:roles.admin")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptTarget(null)} className="border-slate-200">{t("join.cancel")}</Button>
            <Button
              data-testid="confirm-accept-btn"
              onClick={() => decide(acceptTarget.id, true, acceptRole)}
              disabled={busy === acceptTarget?.id}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {busy === acceptTarget?.id ? <Loader2 className="w-4 h-4 animate-spin" /> : t("join.accept")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search & request to join */}
      <div className="glass rounded-2xl p-6" data-testid="join-search-card">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-emerald-500" />
          <h3 className="font-heading text-lg font-semibold">{t("join.searchTitle")}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {t("join.searchHelp")}
        </p>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit mb-3">
          {[{ k: "ref", l: t("join.modeRef") }, { k: "email", l: t("join.modeEmail") }].map((m) => (
            <button key={m.k} data-testid={`join-mode-${m.k}`} onClick={() => { setMode(m.k); setResults(null); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${mode === m.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
              {m.l}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="join-search-input"
            placeholder={mode === "ref" ? "SP-XXXXXX" : t("join.emailPh")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            className="bg-slate-50 border-slate-200"
          />
          <Button data-testid="join-search-btn" onClick={doSearch} disabled={searching}
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold shrink-0">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {results && results.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="join-search-results">
            <div>
              <Label className="text-slate-600 mb-1.5 block">{t("join.message")}</Label>
              <Input data-testid="join-message-input" placeholder={t("join.messagePh")}
                value={message} onChange={(e) => setMessage(e.target.value)} className="bg-slate-50 border-slate-200" />
            </div>
            {results.map((r) => (
              <div key={r.workspace_id} data-testid={`join-result-${r.workspace_id}`}
                className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{r.name}</div>
                  <div className="text-xs text-slate-500 font-mono-token">{r.public_ref} · {r.owner_name || "—"}</div>
                </div>
                {r.already_member ? (
                  <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200 shrink-0">{t("join.alreadyMember")}</Badge>
                ) : (
                  <Button data-testid={`request-join-${r.workspace_id}`} onClick={() => requestJoin(r.workspace_id)}
                    disabled={busy === r.workspace_id} size="sm"
                    className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold shrink-0">
                    {busy === r.workspace_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> {t("join.request")}</>}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My requests */}
      <div className="glass rounded-2xl p-6" data-testid="my-join-requests">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-emerald-500" />
          <h3 className="font-heading text-lg font-semibold">{t("join.mineTitle")}</h3>
        </div>
        {mine.length === 0 ? (
          <p className="text-sm text-slate-500">{t("join.mineEmpty")}</p>
        ) : (
          <div className="space-y-2">
            {mine.map((jr) => {
              const st = STATUS_LABEL[jr.status] || STATUS_LABEL.pending;
              return (
                <div key={jr.id} data-testid={`my-request-${jr.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{jr.workspace_name || "—"}</div>
                    <div className="text-xs text-slate-500 font-mono-token">{jr.workspace_public_ref}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={st.cls}>{t(`join.status.${STATUS_LABEL[jr.status] ? jr.status : "pending"}`)}</Badge>
                    {jr.status === "pending" && (
                      <Button data-testid={`cancel-request-${jr.id}`} onClick={() => cancelMine(jr.id)} disabled={busy === jr.id} size="sm" variant="outline"
                        className="border-slate-200 bg-white hover:bg-slate-100 text-slate-600">
                        {busy === jr.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
